import React, { createContext, useContext } from "react";
import { AppContext, AuthContext, MobileContext } from "App";
import { usePublicData } from "./hooks/usePublicData";
import { useDeviceActions } from "./hooks/useDeviceActions";
import { useUIState } from "./hooks/useUIState";
import MobileView from "./components/MobileView";
import DesktopView from "./components/DesktopView";

export const PublicDataContext = createContext({});

const PublicData = () => {
  const auth = useContext(AuthContext);
  const mobile = useContext(MobileContext);
  const appContext = useContext(AppContext);

  // Custom hooks for data and state management
  const {
    data,
    userDevicesOriginal,
    selectedDevices,
    setSelectedDevices,
    setUserDevicesOriginal,
    calcCenter,
    setCalcCenter,
    calcZoom,
    setCalcZoom,
    deviceTypeToCategory,
    groupedDevices,
  } = usePublicData();

  const {
    getBasicDeviceIcon,
    toggleDevice,
    discardChanges,
    handleSave,
    saveSuccess,
  } = useDeviceActions(
    selectedDevices,
    setSelectedDevices,
    userDevicesOriginal,
    setUserDevicesOriginal
  );

  const {
    popupOpen,
    setPopupOpen,
    showUserMenu,
    setShowUserMenu,
    showTermsOfUse,
    setShowTermsOfUse,
    showPrivacyPolicy,
    setShowPrivacyPolicy,
    showMobileDevices,
    setShowMobileDevices,
    listSelect,
    setListSelect,
    _initialMapCenter,
    _initialMapZoom,
    mapCenter,
    setMapCenter,
    mapZoom,
    setMapZoom,
    showSettingsState,
    setShowSettingsState,
    loginPopupOpen,
    setLoginPopupOpen,
    error,
    setError,
    success,
    setSuccess,
    loginTrigger,
    collapsedGroups,
    collapsedCategories,
    triggerLoginOpen,
    toggleCategoryCollapse,
    toggleGroupCollapse,
    handleLogout,
    explodedGroups,
    setExplodedGroups,
    toggleGroupExploded,
    calculateBounds,
    setMultipleGroupsCollapse,
  } = useUIState(groupedDevices);

  // Common props for both views
  const commonProps = {
    // Auth & User
    auth,
    handleLogout: () => handleLogout(auth),
    loginPopupOpen,
    setLoginPopupOpen,
    showUserMenu,
    setShowUserMenu,
    error,
    setError,
    success,
    setSuccess,
    triggerLoginOpen,

    // Device List
    showMobileDevices,
    setShowMobileDevices,
    showSettingsState,
    setShowSettingsState,
    groupedDevices,
    toggleCategoryCollapse,
    collapsedCategories,
    toggleGroupCollapse,
    collapsedGroups,
    listSelect,
    setListSelect,
    setMapCenter,
    setMapZoom,
    getBasicDeviceIcon,
    selectedDevices,
    toggleDevice,
    handleSave,
    discardChanges,

    // Map
    _initialMapCenter,
    _initialMapZoom,
    mapCenter,
    mapZoom,
    setCalcZoom,
    setCalcCenter,
    calcZoom,
    deviceTypeToCategory,
    popupOpen,
    setPopupOpen,
    saveSuccess,

    // Data
    data,

    // Modals
    showTermsOfUse,
    setShowTermsOfUse,
    showPrivacyPolicy,
    setShowPrivacyPolicy,

    // Exploded groups
    explodedGroups,
    setExplodedGroups,
    toggleGroupExploded,
    calculateBounds,
    setMultipleGroupsCollapse,
  };

  // Mobile view
  if (mobile.mobile) {
    return <MobileView {...commonProps} />;
  }

  // Desktop view
  return (
    <PublicDataContext.Provider
      value={{
        setShowPrivacyPolicy: setShowPrivacyPolicy,
        setShowTermsOfUse: setShowTermsOfUse,
        selectedDevices: selectedDevices,
        setSelectedDevices: setSelectedDevices,
        userDevicesOriginal: userDevicesOriginal,
        setUserDevicesOriginal: setUserDevicesOriginal,
      }}
    >
      <DesktopView
        {...commonProps}
        appContext={appContext}
        loginTrigger={loginTrigger}
      />
    </PublicDataContext.Provider>
  );
};

export default PublicData;
